import classNames from 'classnames';
import { useRouter } from 'next/router';
import ArrowSVG from 'public/icons/arr.svg';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Day } from '@/entities/day';
import { Direction } from '@/entities/direction';
import { Format } from '@/entities/format';
import { Place } from '@/entities/place';
import { transformProgramFromApi } from '@/shared/api/lib/utils/transform-program';
import { createPdf } from '@/shared/api/postFavouritesPrograms';
import { ApiProgramRequest, AppProgramResponse, useProgramQuery } from '@/shared/api/program';
import Dropdown from '@/shared/components/shared/Dropdown';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { ImageShape } from '@/shared/lib/types';
import { encodeUrl } from '@/shared/lib/utils/filter/chpu/encode-url';
import { deleteEmptyFieldsPdf } from '@/shared/lib/utils/pdf/filterPrograms';
import { ConstructorBlock } from '../../constructor/Constructor/Constructor';
import Loader from '../../shared/Loader';
import Reveal from '../../shared/Reveal';
import SearchInput from '../../shared/SearchInput';
import Typografed from '../../shared/Typografed';
import ProgramDetailList from '../ProgramDetailList';

export type ProgramDetailSectionRef = {
    triggerFunction: () => void;
};
export type ProgramDetailCard = {
    id: number;
    time: string;
    day: number;
    slug: string;
    description?: string;
    prefix?: string;
    name: string;
    place: number;
    participationFormats: number[];
    directions: number[];
    companies?: ImageShape[];
    content?: ConstructorBlock[];
};

export type ProgramDetailSectionProps = {
    cards: ProgramDetailCard[];
};

type Props = React.HTMLAttributes<HTMLElement> & {
    reflectFilterStateInUrl?: boolean;
    programDetailSectionProps: ProgramDetailSectionProps;
    isButtonVisible?: boolean;
    initialParams: ApiProgramRequest;
    initialResponse: AppProgramResponse;
    days: Day[];
    formats: Format[];
    places: Place[];
    directions: Direction[];
    disableDirections?: boolean;
    isHrSummit?: boolean;
    visibleCardsCount?: number;
    mainTheme?: string;
};

const ProgramDetailSection = forwardRef<ProgramDetailSectionRef, Props>(
    (
        {
            reflectFilterStateInUrl = false,
            days,
            formats,
            directions,
            programDetailSectionProps,
            isButtonVisible,
            initialResponse,
            places,
            initialParams = {},
            disableDirections = false,
            isHrSummit = false,
            visibleCardsCount = 10,
        },
        ref,
    ) => {
        const router = useRouter();

        const [params, setParams] = useState<ApiProgramRequest>(() => {
            if (typeof window !== 'undefined' && reflectFilterStateInUrl) {
                router.replace(
                    {
                        pathname: router.pathname,
                        query: { ...router.query, ...initialParams },
                    },
                    encodeUrl(router.asPath, initialParams),
                    { shallow: true, scroll: false },
                );
            }

            return initialParams;
        });

        const [searchTerm, setSearchTerm] = useState(params.q || '');
        const debouncedSearchTerm = useDebounce(searchTerm, 500);

        const { data, isFetching, error } = useProgramQuery(
            { params: { ...params, days: typeof params.day === 'number' ? [params.day] : days.map((day) => day.id) } },
            initialResponse,
        );

        const cards = data?.data?.items || programDetailSectionProps.cards;
        const transformedCards = transformProgramFromApi(cards, directions, places, formats, days);

        useImperativeHandle(ref, () => ({
            triggerFunction,
        }));
        const triggerFunction = () => {
            sendRequestPdf();
        };

        function sendRequestPdf() {
            const cards = deleteEmptyFieldsPdf(transformedCards);
            createPdf(cards, 'Программа TNF');
        }

        const handleDayChange = useCallback(
            (dayId?: number) => {
                setParams((prevParams) => {
                    const newParams = { ...prevParams };

                    if (dayId !== undefined) {
                        newParams.day = dayId;
                    } else {
                        delete newParams.day;
                    }

                    if (reflectFilterStateInUrl) {
                        router.push(
                            {
                                pathname: router.pathname,
                                query: { ...router.query, ...newParams },
                            },
                            encodeUrl(router.asPath, newParams),
                            { shallow: true, scroll: false },
                        );
                    }

                    return newParams;
                });
            },
            [reflectFilterStateInUrl],
        );

        const handleDirectionChange = useCallback(
            (directionId: number) => {
                setParams((prevParams) => {
                    const newParams = { ...prevParams };

                    newParams.directions = Array.isArray(newParams.directions) ? [...newParams.directions] : [];

                    if (newParams.directions.includes(directionId)) {
                        newParams.directions = newParams.directions.filter((id) => id !== directionId);
                    } else {
                        newParams.directions.push(directionId);
                    }

                    if (reflectFilterStateInUrl) {
                        router.push(
                            {
                                pathname: router.pathname,
                                query: { ...router.query, ...newParams },
                            },
                            encodeUrl(router.asPath, newParams),
                            { shallow: true, scroll: false },
                        );
                    }

                    return newParams;
                });
            },
            [reflectFilterStateInUrl],
        );

        const handleFormatChange = useCallback(
            (formatId: number) => {
                setParams((prevParams) => {
                    const newParams = { ...prevParams };

                    newParams.participation_formats = Array.isArray(newParams.participation_formats)
                        ? [...newParams.participation_formats]
                        : [];

                    if (newParams.participation_formats.includes(formatId)) {
                        newParams.participation_formats = newParams.participation_formats.filter(
                            (id) => id !== formatId,
                        );
                    } else {
                        newParams.participation_formats.push(formatId);
                    }

                    if (reflectFilterStateInUrl) {
                        router.push(
                            {
                                pathname: router.pathname,
                                query: { ...router.query, ...newParams },
                            },
                            encodeUrl(router.asPath, newParams),
                            { shallow: true, scroll: false },
                        );
                    }

                    return newParams;
                });
            },
            [reflectFilterStateInUrl],
        );

        const handleSearchInput = useCallback((e: any) => {
            setSearchTerm(e.target.value);
        }, []);

        useEffect(() => {
            setParams((prevParams) => {
                const newParams = { ...prevParams };

                if (debouncedSearchTerm) {
                    newParams.q = debouncedSearchTerm;
                } else {
                    delete newParams.q;
                }

                if (reflectFilterStateInUrl) {
                    router.push(
                        {
                            pathname: router.pathname,
                            query: { ...router.query, ...newParams },
                        },
                        encodeUrl(router.asPath, newParams),
                        { shallow: true, scroll: false },
                    );
                }

                return newParams;
            });
        }, [debouncedSearchTerm, reflectFilterStateInUrl]);

        return (
            <>
                <section className="program-detail-section-wrapper wrapper">
                    <div className="news-top-block">
                        <div className="program-filters">
                            {days.length > 0 && (
                                <Dropdown>
                                    <Dropdown.Toggler className="news-select-btn">
                                        <div className="text-xs">
                                            {params.day !== undefined
                                                ? days.find((item) => item.id === params.day)?.day || 'Все дни'
                                                : 'Все дни'}
                                        </div>
                                        <ArrowSVG className="news-arrow-svg" />
                                    </Dropdown.Toggler>
                                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                                        <ul className="list-unstyled menu-dropdown-list">
                                            <li>
                                                <Dropdown.Picker
                                                    onClick={() => handleDayChange(undefined)}
                                                    className={classNames('news-select-item text-xs', {
                                                        'is-active': params.day === undefined,
                                                    })}
                                                >
                                                    Все дни
                                                </Dropdown.Picker>
                                            </li>
                                            {days.map((item, i) => (
                                                <li key={i}>
                                                    <Dropdown.Picker
                                                        onClick={() => handleDayChange(item.id)}
                                                        className={classNames('news-select-item text-xs', {
                                                            'is-active': params.day === item.id,
                                                        })}
                                                    >
                                                        {item.day}
                                                    </Dropdown.Picker>
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown.Content>
                                </Dropdown>
                            )}
                            {formats.length > 0 && (
                                <Dropdown>
                                    <Dropdown.Toggler className="news-select-btn">
                                        <div className="text-xs">Формат участия</div>
                                        <ArrowSVG className="news-arrow-svg" />
                                    </Dropdown.Toggler>
                                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                                        <ul className="list-unstyled menu-dropdown-list">
                                            {formats.map((format) => (
                                                <li key={format.id}>
                                                    <Dropdown.Picker
                                                        className={classNames('news-select-item text-xs', {
                                                            'is-active': params.participation_formats?.includes(
                                                                format.id,
                                                            ),
                                                        })}
                                                        onClick={() => handleFormatChange(format.id)}
                                                        keepOpen={true}
                                                        key={format.id}
                                                    >
                                                        {format.name}
                                                    </Dropdown.Picker>
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown.Content>
                                </Dropdown>
                            )}
                            {!disableDirections && !isHrSummit && directions.length > 0 && (
                                <Dropdown>
                                    <Dropdown.Toggler className="news-select-btn">
                                        <div className="text-xs">Направление</div>
                                        <ArrowSVG className="news-arrow-svg" />
                                    </Dropdown.Toggler>
                                    <Dropdown.Content className="news-filter__content" position="bottom-right">
                                        <ul className="list-unstyled menu-dropdown-list">
                                            {directions?.map((direction) => (
                                                <li key={direction.id}>
                                                    <Dropdown.Picker
                                                        className={classNames('news-select-item text-xs', {
                                                            'is-active': params.directions?.includes(direction.id),
                                                        })}
                                                        onClick={() => handleDirectionChange(direction.id)}
                                                        keepOpen
                                                    >
                                                        {direction.name}
                                                    </Dropdown.Picker>
                                                </li>
                                            ))}
                                        </ul>
                                    </Dropdown.Content>
                                </Dropdown>
                            )}
                        </div>
                        <SearchInput value={searchTerm} onInput={handleSearchInput} placeholder="Поиск по программе" />
                    </div>
                    {transformedCards.length > 0 ? (
                        <ProgramDetailList
                            isFetching={isFetching}
                            cards={transformedCards}
                            isButtonVisible={isButtonVisible}
                            visibleCardsCount={visibleCardsCount}
                            disableDirections={disableDirections}
                        />
                    ) : (
                        !isFetching && (
                            <Reveal>
                                <div className="text-l news-list-not-found">
                                    <Typografed>
                                        {Object.values({ ...params, limit: null }).filter(
                                            (value) =>
                                                Boolean(value) && (Array.isArray(value) ? value.length > 0 : true),
                                        ).length > 0
                                            ? 'Ничего не найдено. Попробуйте изменить запрос и мы поищем ещё раз. '
                                            : 'Программы не найдены'}
                                    </Typografed>
                                </div>
                            </Reveal>
                        )
                    )}
                </section>
                <Loader isFetching={isFetching} />
            </>
        );
    },
);

ProgramDetailSection.displayName = 'ProgramDetailSection';

export default ProgramDetailSection;
